import { useCallback, useMemo, useRef } from "react"
import type { CognitoClientOptions } from "./CognitoClientOptions"
import { fetchTokenWithCode } from "./fetchTokenWithCode"
import { fetchTokenWithRefreshToken } from "./fetchTokenWithRefreshToken"
import { getAuthorizeUrl } from "./getAuthorizeUrl"
import { isTokenExpired } from "./isTokenExpired"
import { parseCognitoQueryParams } from "./parseQueryParams"

export type AuthorizationHeaderFunction = () => Promise<AuthorizationHeader>
export enum RequestType {
	FETCH_TOKEN_BY_CODE = "FETCH_TOKEN_BY_CODE",
	FETCH_TOKEN_BY_REFRESH_TOKEN = "FETCH_TOKEN_BY_REFRESH_TOKEN",
}

export interface AuthorizationHeader {
	Authorization: string
}

export const useCognito = (
	cognitoConfig: CognitoClientOptions,
	onError?: (requestType: RequestType, error: unknown) => void,
) => {
	const { code, state } = useMemo(() => parseCognitoQueryParams(window.location.search), [])
	const pendingRequestRef = useRef<Promise<AuthorizationHeader> | null>(null)
	const onErrorRef = useRef(onError)

	onErrorRef.current = onError

	const getAuthorizationHeader: AuthorizationHeaderFunction = useCallback(async () => {
		if (pendingRequestRef.current) {
			return pendingRequestRef.current
		}

		const executeRequest = async (): Promise<AuthorizationHeader> => {
			const idToken = window.localStorage.getItem("id_token")
			if (idToken && !isTokenExpired(idToken)) {
				return { Authorization: `Bearer ${idToken}` }
			}

			if (code) {
				// Fetch Token with the provided code
				try {
					const { data } = await fetchTokenWithCode(cognitoConfig, code)

					window.localStorage.setItem("id_token", data.id_token)
					window.localStorage.setItem("refresh_token", data.refresh_token)
					if (state) {
						window.location.assign(decodeURIComponent(state))
					}
					return { Authorization: `Bearer ${data.id_token}` }
				} catch (error) {
					console.error("Error fetching id token with code", JSON.stringify(error))
					onErrorRef.current?.(RequestType.FETCH_TOKEN_BY_CODE, error)
					return Promise.reject(new Error("Error fetching id token with code"))
				}
			}

			const refreshToken = window.localStorage.getItem("refresh_token")
			// Refresh invalid or expired token by refreshtoken
			if (refreshToken && (!idToken || isTokenExpired(idToken))) {
				try {
					const { data } = await fetchTokenWithRefreshToken(cognitoConfig, refreshToken)
					window.localStorage.setItem("id_token", data.id_token)
					return { Authorization: `Bearer ${data.id_token}` }
				} catch (error) {
					onErrorRef.current?.(RequestType.FETCH_TOKEN_BY_REFRESH_TOKEN, error)
					window.location.assign(getAuthorizeUrl(cognitoConfig))
					return { Authorization: "" }
				}
			}
			if (!idToken) {
				window.location.assign(getAuthorizeUrl(cognitoConfig))
				return { Authorization: "" }
			}
			return Promise.reject(new Error("No valid authentication method found"))
		}

		pendingRequestRef.current = executeRequest().finally(() => {
			pendingRequestRef.current = null
		})

		return pendingRequestRef.current
	}, [code, cognitoConfig, state])

	return {
		getAuthorizationHeader,
	}
}
